import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import Xarrow, { Xwrapper } from "react-xarrows";
import {
  Box,
  TextField,
  Grid,
  IconButton,
  Popover,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import ChatbotPreview from "./ChatbotPreview";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import EmojiPicker from "emoji-picker-react";
import MDButton from "components/MDButton";
import ColorInputField from "./ColorInputField";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const exampleAvatars = [
  "https://rieshim.app/uploads/example_avatar_1.jpeg",
  "https://rieshim.app/uploads/example_avatar_2.jpeg",
  "https://rieshim.app/uploads/example_avatar_3.jpeg",
  "https://rieshim.app/uploads/example_avatar_4.jpeg",
  "https://rieshim.app/uploads/example_avatar_5.png",
  "https://rieshim.app/uploads/example_avatar_6.jpeg",
];

function BasicInfoStep({
  name,
  setName,
  welcomeMessage,
  setWelcomeMessage,
  headerColor,
  setHeaderColor,
  buttonColor,
  setButtonColor,
  textColor,
  setTextColor,
  backgroundColor,
  setBackgroundColor,
  avatarUrl,
  handleAvatarUrlSelect,
  handleAvatarUpload,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const avatarInputRef = useRef(null);

  const handleEmojiClick = (emojiObject) => {
    setWelcomeMessage((prev) => prev + emojiObject.emoji);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "emoji-popover" : undefined;

  return (
    <Box>
      <Xwrapper>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              id="name-input-anchor"
              sx={{ mb: 3, borderLeft: "4px solid", borderColor: "#5a49da" }}
            >
              <CardContent sx={{ pt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <SmartToyOutlinedIcon sx={{ mr: 1, color: "#5a49da" }} />
                  <MDTypography variant="h6" fontWeight="bold">
                    Chatbot Identity
                  </MDTypography>
                </Box>
                <TextField
                  fullWidth
                  label="Name of your chatbot"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 3 }}
                />
                <MDTypography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
                  Avatar
                </MDTypography>
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  {exampleAvatars.map((url) => (
                    <Avatar
                      key={url}
                      src={url}
                      sx={{
                        cursor: "pointer",
                        width: 56,
                        height: 56,
                        border: avatarUrl === url ? "3px solid" : "3px solid transparent",
                        borderColor: avatarUrl === url ? "primary.main" : "transparent",
                        "&:hover": {
                          borderColor: "primary.light",
                        },
                      }}
                      onClick={() => handleAvatarUrlSelect(url)}
                    />
                  ))}
                </Box>
                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
                  startIcon={<DriveFolderUploadOutlinedIcon />}
                  sx={{
                    py: 1.5,
                    borderStyle: "dashed",
                    "&:hover": {
                      borderStyle: "dashed",
                    },
                  }}
                >
                  Upload Custom Avatar
                </MDButton>
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  hidden
                />
              </CardContent>
            </Card>

            <Card
              sx={{ mb: 3, borderLeft: "4px solid", borderColor: "info.main" }}
              id="welcome-message-input-anchor"
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ChatOutlinedIcon sx={{ mr: 1, color: "info.main" }} />
                  <MDTypography variant="h6" fontWeight="bold">
                    Welcome Message
                  </MDTypography>
                </Box>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    label="Welcome message"
                    multiline
                    rows={4}
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    variant="outlined"
                  />
                  <IconButton
                    aria-describedby={id}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{ position: "absolute", right: 8, bottom: 8 }}
                    size="small"
                  >
                    <EmojiEmotionsIcon />
                  </IconButton>
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </Popover>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderLeft: "4px solid", borderColor: "warning.main" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PaletteOutlinedIcon sx={{ mr: 1, color: "warning.main" }} />
                  <MDTypography variant="h6" fontWeight="bold">
                    Appearance
                  </MDTypography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ColorInputField
                      label="Header Color"
                      color={headerColor}
                      setColor={setHeaderColor}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorInputField
                      label="Button Color"
                      color={buttonColor}
                      setColor={setButtonColor}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorInputField label="Text Color" color={textColor} setColor={setTextColor} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorInputField
                      label="Background Color"
                      color={backgroundColor}
                      setColor={setBackgroundColor}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
            <Card
              id="preview-anchor"
              sx={{
                height: "96.7%",
                borderRight: "4px solid",
                borderColor: "success.main",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <VisibilityOutlinedIcon sx={{ mr: 1, color: "success.main" }} />
                  <MDTypography variant="h6" fontWeight="bold">
                    Live Preview
                  </MDTypography>
                </Box>
                <ChatbotPreview
                  name={name}
                  welcomeMessage={welcomeMessage}
                  headerColor={headerColor}
                  buttonColor={buttonColor}
                  textColor={textColor}
                  backgroundColor={backgroundColor}
                  avatarUrl={avatarUrl}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Xarrow
          start="name-input-anchor"
          end="preview-name-anchor"
          startAnchor="right"
          endAnchor="left"
          color="rgba(90, 73, 218, 0.7)"
          strokeWidth={2}
          path="smooth"
          curveness={0.8}
          headSize={4}
          dashness={{ animation: 1 }}
        />
        <Xarrow
          start="welcome-message-input-anchor"
          end="preview-welcome-anchor"
          startAnchor="right"
          endAnchor="left"
          color="rgba(90, 73, 218, 0.7)"
          strokeWidth={2}
          path="smooth"
          curveness={0.8}
          headSize={4}
          dashness={{ animation: 1 }}
        />
      </Xwrapper>
    </Box>
  );
}

BasicInfoStep.propTypes = {
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  welcomeMessage: PropTypes.string.isRequired,
  setWelcomeMessage: PropTypes.func.isRequired,
  headerColor: PropTypes.string.isRequired,
  setHeaderColor: PropTypes.func.isRequired,
  buttonColor: PropTypes.string.isRequired,
  setButtonColor: PropTypes.func.isRequired,
  textColor: PropTypes.string.isRequired,
  setTextColor: PropTypes.func.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  setBackgroundColor: PropTypes.func.isRequired,
  avatarUrl: PropTypes.string,
  handleAvatarUrlSelect: PropTypes.func.isRequired,
  handleAvatarUpload: PropTypes.func.isRequired,
};

export default BasicInfoStep;
